/**
 * Noise Layer Helpers
 *
 * Utilities for creating Mapbox GL JS expressions for noise H3 hexagon vector tiles
 */

import type { DynamicLayerConfig } from '../config/dynamicLayersConfig'

/**
 * Mapping from layer ID to Elasticsearch index name
 */
const INDEX_MAP: Record<string, string> = {
  'noise-road-day': 'noise-road-day-v1',
  'noise-road-night': 'noise-road-night-v1',
  'noise-railway-day': 'noise-railway-day-v1',
  'noise-railway-night': 'noise-railway-night-v1',
}

/**
 * dB value ranges for noise layers
 * Based on the improved color scale for better low/high noise differentiation
 * < 30 dB: Very quiet (Light blue)
 * 30-40 dB: Quiet (Teal)
 * 40-50 dB: Moderate (Yellow-green)
 * 50-60 dB: Noticeable (Yellow)
 * 60-70 dB: Loud (Orange)
 * 70-80 dB: Very loud (Red-orange)
 * 80-100 dB: Harmful (Red)
 */
export const NOISE_DB_RANGES = [
  0, // < 30 dB
  30, // 30-40 dB
  40, // 40-50 dB
  50, // 50-60 dB
  60, // 60-70 dB
  70, // 70-80 dB
  80, // 80-100 dB
  100,
]

const VALUE_RANGES: Record<string, number[]> = {
  'noise-road-day': NOISE_DB_RANGES,
  'noise-road-night': NOISE_DB_RANGES,
  'noise-railway-day': NOISE_DB_RANGES,
  'noise-railway-night': NOISE_DB_RANGES,
}

/**
 * Color scheme for noise layers
 * Improved color scale matching the screenshot for better differentiation
 * between low noise (blue tones) and high noise (red tones)
 */
export const NOISE_COLORS = [
  '#a8d5e2', // < 30 dB - Light blue (very quiet)
  '#6cb5c4', // 30-40 dB - Teal (quiet)
  '#c8d96f', // 40-50 dB - Yellow-green (moderate)
  '#f9d84a', // 50-60 dB - Yellow (noticeable)
  '#f5a142', // 60-70 dB - Orange (loud)
  '#f16c4d', // 70-80 dB - Red-orange (very loud)
  '#e94b3c', // 80-100 dB - Red (harmful)
  '#e94b3c', // 100 dB - Red
]

/**
 * Check if a layer is a noise layer
 */
export function isNoiseLayer(layerId: string): boolean {
  return layerId.startsWith('noise-')
}

/**
 * Get the Elasticsearch index name for a noise layer
 */
export function getNoiseIndex(layerId: string): string | undefined {
  return INDEX_MAP[layerId]
}

/**
 * Get the aggregation field name for noise layers
 * Elasticsearch searchMvt returns aggregations with the field under .value property
 */
export function getNoiseField(layerId: string): string {
  return 'avg.value'
}

/**
 * Create Mapbox fill-color expression for a noise layer
 *
 * Returns a Mapbox expression that maps dB values to colors
 */
export function createNoiseFillColorExpression(
  layerId: string,
  layerConfig: DynamicLayerConfig
): any {
  const colors = layerConfig.render?.colors || []
  const field = getNoiseField(layerId)
  const values = VALUE_RANGES[layerId]

  if (!values) {
    console.warn(`No value range defined for noise layer: ${layerId}`)
    return colors[0] || '#CCCCCC'
  }

  // Build interpolation stops: value1, color1, value2, color2, ...
  const stops: any[] = []
  for (let i = 0; i < Math.min(values.length, colors.length); i++) {
    stops.push(values[i], colors[i])
  }

  return ['interpolate', ['linear'], ['get', field], ...stops]
}

/**
 * Format a noise value for display in tooltips
 */
export function formatNoiseValue(
  layerId: string,
  value: number | string | undefined
): string {
  if (value === undefined || value === null) {
    return 'No data'
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(numValue)) {
    return 'No data'
  }

  // Format with 1 decimal place and dB unit
  return `${numValue.toFixed(1)} dB`
}

/**
 * Get the display name for a noise layer (for tooltip title)
 */
export function getNoiseLayerDisplayName(layerId: string): string {
  const names: Record<string, string> = {
    'noise-road-day': 'Road Noise - Day',
    'noise-road-night': 'Road Noise - Night',
    'noise-railway-day': 'Railway Noise - Day',
    'noise-railway-night': 'Railway Noise - Night',
  }
  return names[layerId] || layerId
}

/**
 * Get all noise layer IDs
 */
export function getAllNoiseLayerIds(): string[] {
  return Object.keys(INDEX_MAP)
}

/**
 * Check if a layer is a road noise layer
 */
export function isRoadNoiseLayer(layerId: string): boolean {
  return layerId.startsWith('noise-road-')
}

/**
 * Check if a layer is a railway noise layer
 */
export function isRailwayNoiseLayer(layerId: string): boolean {
  return layerId.startsWith('noise-railway-')
}

/**
 * Noise layer Z-ordering (bottom to top)
 * Later items in array render on top
 */
export const NOISE_LAYER_ORDER = [
  'noise-railway-night', // Bottom
  'noise-railway-day',
  'noise-road-night',
  'noise-road-day', // Top (default visible)
]

/**
 * Get API URL for noise vector tiles
 */
export function getNoiseVectorTileUrl(
  apiBaseUrl: string,
  index: string
): string {
  return `${apiBaseUrl}/noise/tiles/{z}/{x}/{y}?index=${index}`
}

/**
 * Noise vector tile source configuration
 */
export function createNoiseVectorTileSource(apiBaseUrl: string, index: string) {
  return {
    type: 'vector' as const,
    tiles: [getNoiseVectorTileUrl(apiBaseUrl, index)],
    minzoom: 10,
    maxzoom: 16,
  }
}
